import dayjs from "dayjs";

import {
  habitTaskWindow,
  HabitCycleType,
  HabitTimeType,
  type HabitCycle,
  type HabitSchedule,
  type HabitTime,
} from "@fitness-app/data-models/entities/Habit";

import { habitCycleTypeLabels, weekdayOptions } from "./constants";

/**
 * "Codziennie" when all 7 days are selected, otherwise a list of weekday chips like "Pn, Śr, Pt".
 * TRE-1656 — dated tasks render their date window instead ("15.08.2026" / "10.08 – 15.08.2026").
 */
export const formatSchedule = (schedule?: HabitSchedule | null): string => {
  const window = schedule ? habitTaskWindow(schedule) : null;
  if (window) {
    if (window.start === window.end) {
      return dayjs(window.end).format("DD.MM.YYYY");
    }
    const start = dayjs(window.start);
    const end = dayjs(window.end);
    // A range crossing New Year must keep the start year, or "31.12 – 02.01.2027" reads as one year.
    return `${start.format(start.year() === end.year() ? "DD.MM" : "DD.MM.YYYY")} – ${end.format("DD.MM.YYYY")}`;
  }

  const days = schedule?.daysOfWeek ?? [];

  if (days.length === 0) {
    return "Brak dni";
  }

  if (days.length === 7) {
    return "Codziennie";
  }

  return weekdayOptions
    .filter((option) => days.includes(option.value))
    .map((option) => option.label)
    .join(", ");
};

export const formatCycle = (cycle?: HabitCycle | null): string => {
  if (!cycle) {
    return "-";
  }

  switch (cycle.type) {
    case HabitCycleType.UntilDate:
      return `${habitCycleTypeLabels[HabitCycleType.UntilDate]}: ${cycle.untilDate}`;
    case HabitCycleType.CycleNumber:
      return `${cycle.totalCycles} × ${cycle.cycleLength} dni`;
    case HabitCycleType.Infinite:
    default:
      return habitCycleTypeLabels[HabitCycleType.Infinite];
  }
};

export const formatTime = (time?: HabitTime | null): string => {
  if (!time) {
    return "-";
  }

  switch (time.type) {
    case HabitTimeType.Hour:
      return time.hour;
    case HabitTimeType.Range:
      return `${time.start} – ${time.end}`;
    case HabitTimeType.AllDay:
    default:
      return "Cały dzień";
  }
};
